import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';

import { useModalManager } from '../../../../hooks/ModalManager.hook';
import { useAppDispatch } from '../../../../hooks/useAppDispatcher.hook';

import {
  createSourceControlThunk,
  deleteSourceControlThunk,
  getSourceControlListThunk,
  updateSourceControlThunk,
} from '../../../../redux/ControlPanel/SourceControl/SourceControl.thunk';

import CardComponent from '../../../../components/Card.component';
import ButtonComponent from '../../../../components/Button.component';
import InputComponent from '../../../../components/Input/Input.component';
import SpinnerComponent from '../../../../components/Spinner.component';
import ControlCreateEditModal from '../../../../components/Modal/Control/ControlCreateEditModal';
import ConfirmDuplicateModal from '../../../../components/Modal/Control/ConfirmDuplicateModal';
import ControlSectionContent from './ControlSectionContent.component';
import { SortTypes } from '../../../../types/sort.types';

import SearchIcon from '../../../../components/Icons/Search.icon';
import SortArrowsIcon from '../../../../components/Icons/SortArrows.icon';

import {
  StyledControlListItem,
  StyledControlSectionContainer,
  StyledControlSectionHeader,
  StyledHeaderButtonsWrapper,
  StyledHeaderLabel,
  StyledHeaderTitle,
  StyledSearchIcon,
  StyledSearchWrapper,
  StyledSortContainer,
} from './sharedStyledComponents';
import { checkUserRequiredPermissions } from '../../../../utils/getUserRolesByToken';
import { UserPermissions } from '../../../../types/userPermissions.types';
import useAuthHook from '../../../../hooks/useAuth.hook';
import { ControlSectionTypes } from '../../../../redux/ControlPanel/ControlPanelList/ControlPanelList.type';
import { useSelector } from 'react-redux';
import {
  getSourceControlList,
  getSourceControlListPagination,
  setSourceControlList,
} from '../../../../redux/ControlPanel/SourceControl/SourceControl.slice';
import { Source } from '../../../../redux/Source/Source.type';

const sectionType = ControlSectionTypes.SOURCES;

export const SourcesSectionComponent: FC = () => {
  const { user } = useAuthHook();
  const dispatch = useAppDispatch();
  const { openModal } = useModalManager();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [orderSort, setOrderSort] = useState(SortTypes.ASC);

  const sources = useSelector(getSourceControlList);
  const pagination = useSelector(getSourceControlListPagination);

  const editable = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_EDIT_SOURCE,
  ]);

  const creatable = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_CREATE_SOURCE,
  ]);

  const deletable = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_DELETE_SOURCE,
  ]);

  const viewable = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_VIEW_SOURCE,
  ]);

  const fetchParams = useMemo(() => {
    return {
      q: searchQuery,
      sort: `${orderSort === SortTypes.DESC ? '-' : ''}name`,
    };
  }, [orderSort, searchQuery]);

  const createItemWithModalWrapper = async (
    fetchFunc: Function,
    updateFunc: Function,
    payload: any,
    updateFetchParams?: any,
    message?: string,
  ) => {
    dispatch(fetchFunc(payload)).then((res: any) => {
      if (!res.payload && !!res.error) {
        openModal(ConfirmDuplicateModal, {
          message: message ?? t(`Such a record already exists!`),
        });
      } else {
        dispatch(updateFunc(updateFetchParams));
      }
    });
  };

  const updateListAfterAction = () => {
    dispatch(getSourceControlListThunk(fetchParams));
  };

  const createNewSectionItem = (newItem: Source) => {
    createItemWithModalWrapper(
      createSourceControlThunk,
      getSourceControlListThunk,
      {
        name: newItem.name,
        code: newItem.name,
      },
      fetchParams,
    );
  };

  const editSectionItem = (sectionItem: Source) => {
    createItemWithModalWrapper(
      updateSourceControlThunk,
      getSourceControlListThunk,
      sectionItem,
      fetchParams,
    );
  };

  const deleteSectionItem = (sectionItem: Source) => {
    dispatch(deleteSourceControlThunk(sectionItem.id)).then(() =>
      updateListAfterAction(),
    );
  };

  const handleSortChange = (sortOrder: SortTypes) => {
    handleSort(sortOrder);

    dispatch(
      getSourceControlListThunk({
        page: 1,
        q: searchQuery,
        sort: `${sortOrder === SortTypes.ASC ? '-' : ''}name`,
      }),
    );
  };

  const handleSearch = (value: string) => {
    setLoading(true);

    dispatch(
      getSourceControlListThunk({
        q: value,
        page: 1,
        sort: `${orderSort === SortTypes.DESC ? '-' : ''}name`,
      }),
    ).finally(() => setLoading(false));
  };

  const debounceSearchValue = useCallback(
    debounce(handleSearch, 300) as Function,
    [],
  );

  const handleSort = (sortOrder: SortTypes) => {
    setOrderSort(sortOrder === SortTypes.ASC ? SortTypes.DESC : SortTypes.ASC);
  };

  const updateItems = (items: Source[]) => {
    dispatch(setSourceControlList(items));
  };

  useEffect(() => {
    dispatch(getSourceControlListThunk(fetchParams));
  }, []);

  if (!viewable) return null;

  return (
    <CardComponent>
      <StyledControlSectionContainer>
        <StyledControlSectionHeader>
          <StyledHeaderTitle>
            <StyledHeaderLabel>{t('Source management')}</StyledHeaderLabel>

            <StyledSortContainer onClick={() => handleSortChange(orderSort)}>
              <SortArrowsIcon sortOrder={orderSort} svgId={sectionType} />
            </StyledSortContainer>
          </StyledHeaderTitle>

          <StyledHeaderButtonsWrapper>
            {creatable && (
              <ButtonComponent
                handleClick={() =>
                  openModal(ControlCreateEditModal, {
                    sectionType,
                    createNewSectionItem,
                    editable,
                    deletable,
                  })
                }
                textColor="#588fff"
                customColor="#ebf0ff"
              >
                {t('Create')}
              </ButtonComponent>
            )}
          </StyledHeaderButtonsWrapper>
        </StyledControlSectionHeader>

        <StyledSearchWrapper>
          <InputComponent
            value={searchQuery}
            onChange={(searchValue: string) => {
              setSearchQuery(searchValue);
              debounceSearchValue(searchValue);
            }}
            onEnter={handleSearch}
            placeholder={t(`Search ${sectionType}`)}
            right={
              <StyledSearchIcon>
                {isLoading ? <SpinnerComponent size="small" /> : <SearchIcon />}
              </StyledSearchIcon>
            }
          />
        </StyledSearchWrapper>
        <ControlSectionContent
          isLoadMode={!(pagination?.currentPage === pagination?.lastPage)}
          initialData={sources}
          fetchData={getSourceControlListThunk}
          sectionType={sectionType}
          fetchParams={{
            q: searchQuery,
            sort: `${orderSort === SortTypes.DESC ? '-' : ''}name`,
            page: pagination?.currentPage || 1,
          }}
          updateItems={updateItems}
          renderItem={item => (
            <StyledControlListItem
              key={item.id}
              highlighted={item.isAllRegions}
              onClick={() =>
                openModal(ControlCreateEditModal, {
                  sectionType,
                  sectionItem: item,
                  editSectionItem,
                  deleteSectionItem,
                  editable,
                  deletable,
                })
              }
            >
              {item.truncatedDisplayName || item.name}
            </StyledControlListItem>
          )}
        />
      </StyledControlSectionContainer>
    </CardComponent>
  );
};
